import { ReactNode } from 'react';

export interface FAQItem {
  question: string;
  answer: string | ReactNode;
  tags: string[];
}

export const allFAQs: FAQItem[] = [
  {
    question: "How is Cutshort different from Naukri and LinkedIn?",
    answer: "Naukri and LinkedIn give you a large pool of applicants to filter through. Cutshort gives you a shortlist of candidates who actually fit your role — so you spend less time screening and more time interviewing the right people. Same category, better starting point.",
    tags: ["home", "automatic-sourcing"]
  },
  {
    question: "How is Cutshort different from recruitment agencies?",
    answer: "Recruitment agencies charge high percentage-based fees and rely on the same generic job boards everyone else uses. They lack the depth to properly evaluate engineering roles. Cutshort is built around understanding tech roles — with relevance and quality checks that conventional agencies miss. The result: better funnel conversion, higher joining ratios, and faster closures at a fraction of the cost.",
    tags: ["home", "assisted-models"]
  },
  {
    question: "Which kind of roles does Cutshort work for?",
    answer: "Cutshort is built for tech and product hiring — roles like Software Engineering, Data Science, DevOps, Product Management, and UI/UX Design, typically in the 1–12 year experience range. These are roles where generic job boards flood you with irrelevant profiles. Cutshort cuts through that noise.",
    tags: ["general"]
  },
  {
    question: "What kind of companies use Cutshort?",
    answer: "Companies that care about quality of hire over volume of applications — from fast-growing startups like Sprinto and Shiprocket to GCCs like Deepintent and consulting firms like Thoughtworks. They use Cutshort because they'd rather act on 10 strong-fit candidates than filter through 500 applications.",
    tags: ["general"]
  },
  {
    question: "How does Cutshort pricing work?",
    answer: "Cutshort offers flexible pricing depending on how you want to hire. You can subscribe and use the platform with your own team, or have our team deliver shortlisted candidates for your roles on a success-fee basis. Talk to us to find the right fit for your hiring needs.",
    tags: ["home", "ta-leaders"]
  },
  {
    question: "How fast can I start hiring on Cutshort?",
    answer: "Most companies see their first shortlist within 24–48 hours of posting a role. In many cases, candidates are ready for review the same day. Whether you're hiring yourself or working with our team, the goal is the same — get to interviews faster, not pile up profiles for you to filter.",
    tags: ["home", "general"]
  },
  {
    question: "Can I try Cutshort for free?",
    answer: "Yes. You can start a free trial of the subscription model — post roles, see candidate matches, and experience the shortlist quality firsthand. No payment required to get started.",
    tags: ["home", "general"]
  },
  {
    question: "How does Cutshort ensure candidate quality?",
    answer: "It starts with the pool itself — Cutshort attracts professionals who are interested in product companies and startups, people looking for meaningful work, not just any opening. From there, candidates are evaluated on quality signals that generic job boards ignore: career growth trajectory, salary progression, tenure patterns, educational background, and experience at funded startups. When needed, candidates also go through a screening conversation that validates fit beyond what a resume can show. The result is a shortlist you can act on with confidence, not just a list of keyword matches.",
    tags: ["home", "general"]
  },
  {
    question: "We use Naukri. Can Cutshort replace Naukri?",
    answer: "If you have very high hiring volume, Naukri offers a diverse resume database that's quite useful. But companies also invest in Cutshort for its relevance and speed advantage when it comes to hiring skilled engineering talent. On pricing, Naukri charges per user login and has several hidden factors that push costs up. Cutshort pricing is one small flat fee that depends on your company size — simple and predictable.",
    tags: ["home", "general"]
  },
  {
    question: "Is Cutshort an ATS?",
    answer: "No. Cutshort is a hiring platform focused on finding candidates who fit your roles. It integrates with popular ATS platforms so you can sync candidates into your existing workflow without any disruption.",
    tags: ["general", "automatic-sourcing"]
  },
  {
    question: "Will Cutshort integrate with my ATS?",
    answer: "We support 50+ ATS systems including Lever, Greenhouse, Freshteams, Keka & Workable. Our optional Smart ATS Sync feature sends only top candidates for your job roles automatically to your ATS job pipeline.",
    tags: ["automatic-sourcing"]
  },
  {
    question: "Are there any usage limits in Startup Support Plan?",
    answer: "Each monthly cycle includes 250 unlocks, designed to help you close around 10 offers per month. Salary limit for candidates is ₹70 LPA.",
    tags: ["startup-offering"]
  },
  {
    question: "What is the eligibility for Startup Support Plan?",
    answer: "This plan is exclusively for small startups with less than $1M in funding and fewer than 50 employees.",
    tags: ["startup-offering"]
  },
  {
    question: "I need a recruiter. Do you provide one?",
    answer: "You can explore our Assisted model: https://cutshort.ai/assisted-sourcing-models",
    tags: ["startup-offering"]
  }
];

/**
 * Filter FAQs by tags
 * @param tags - Array of tags to filter by. FAQs matching ANY of the provided tags will be returned.
 * @returns Filtered array of FAQs
 */
export const getFAQsByTags = (tags: string[]): FAQItem[] => {
  if (!tags || tags.length === 0) {
    return allFAQs.filter(faq => faq.tags.includes("general"));
  }
  
  return allFAQs.filter(faq => 
    tags.some(tag => faq.tags.includes(tag))
  );
};
